import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";

export const useMagicLinkProcessor = () => {
  const [processingMagicLink, setProcessingMagicLink] = useState(false);
  const [processingInitialized, setProcessingInitialized] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    // Only run once
    if (processingInitialized) return;
    setProcessingInitialized(true);
    
    const processToken = async () => {
      // First check if we have a token in the URL
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const type = params.get('type');
      
      if (token && type === 'recovery') {
        console.log("Found recovery token in URL, processing...");
        setProcessingMagicLink(true);
        
        try {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: 'recovery',
          });
          
          if (error) {
            console.error('Error verifying token:', error);
            setTokenError(error.message);
            toast.error(`Failed to verify recovery link: ${error.message}`);
          } else if (data?.user) {
            console.log('Token verified successfully:', data.user.id);
            toast.success("Please set a new password to secure your account.");
            
            // Store the password reset state in localStorage
            localStorage.setItem('passwordResetRequired', 'true');
            console.log('Set passwordResetRequired flag in localStorage');
            
            const { data: sessionData } = await supabase.auth.getSession();
            if (sessionData?.session) {
              console.log("Session established after token verification:", sessionData.session.user.id);
              // Keep user on the auth page with the recovery flag
              const cleanUrl = `${window.location.pathname}?type=recovery`;
              window.history.replaceState({}, document.title, cleanUrl);
            }
          }
        } catch (e) {
          console.error("Error processing recovery token:", e);
          setTokenError("An unexpected error occurred while processing your password reset link. Please try again.");
        } finally {
          setProcessingMagicLink(false);
          
          // Keep the recovery parameter but remove the token
          const cleanUrl = `${window.location.pathname}?type=recovery`;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      } else {
        // Check if type=recovery is in the URL without a token
        // This means we've already processed the token
        if (type === 'recovery') {
          console.log("Recovery type parameter found without token, user is in password reset flow");
          localStorage.setItem('passwordResetRequired', 'true');
        }
      }
    };
    
    // Delay processing by a small amount to allow auth to initialize
    const timer = setTimeout(() => {
      processToken();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [location, processingInitialized]);

  return { processingMagicLink, tokenError, setTokenError };
};
